import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Cookie, X, Settings, Shield } from 'lucide-react';

const CONSENT_KEY = 'parler_cloud_cookie_consent';
const CONSENT_PREFERENCES_KEY = 'parler_cloud_cookie_preferences';

export const CookieConsentBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always true, cannot be disabled
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    // Check if user has already given consent
    const consent = localStorage.getItem(CONSENT_KEY);
    if (!consent) {
      setShowBanner(true);
      // Disable Google Analytics by default until consent
      window['ga-disable-G-41FMTVX9LS'] = true;
    } else {
      // Load saved preferences
      const savedPreferences = localStorage.getItem(CONSENT_PREFERENCES_KEY);
      if (savedPreferences) {
        const parsed = JSON.parse(savedPreferences);
        setPreferences(parsed);
        // Enable/disable GA based on saved preference
        window['ga-disable-G-41FMTVX9LS'] = !parsed.analytics;
        if (parsed.analytics) {
          enableGoogleAnalytics();
        }
      }
    }
  }, []);

  const enableGoogleAnalytics = () => {
    window['ga-disable-G-41FMTVX9LS'] = false;
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'granted'
      });
    }
  };

  const disableGoogleAnalytics = () => {
    window['ga-disable-G-41FMTVX9LS'] = true;
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'denied'
      });
    }
  };

  const handleAcceptAll = () => {
    const newPreferences = {
      necessary: true,
      analytics: true,
      marketing: true,
    };
    setPreferences(newPreferences);
    localStorage.setItem(CONSENT_KEY, 'true');
    localStorage.setItem(CONSENT_PREFERENCES_KEY, JSON.stringify(newPreferences));
    enableGoogleAnalytics();
    setShowBanner(false);
    setShowPreferences(false);
  };

  const handleRejectAll = () => {
    const newPreferences = {
      necessary: true,
      analytics: false,
      marketing: false,
    };
    setPreferences(newPreferences);
    localStorage.setItem(CONSENT_KEY, 'true');
    localStorage.setItem(CONSENT_PREFERENCES_KEY, JSON.stringify(newPreferences));
    disableGoogleAnalytics();
    setShowBanner(false);
    setShowPreferences(false);
  };

  const handleSavePreferences = () => {
    localStorage.setItem(CONSENT_KEY, 'true');
    localStorage.setItem(CONSENT_PREFERENCES_KEY, JSON.stringify(preferences));
    if (preferences.analytics) {
      enableGoogleAnalytics();
    } else {
      disableGoogleAnalytics();
    }
    setShowBanner(false);
    setShowPreferences(false);
  };

  const handlePreferenceChange = (key) => {
    if (key === 'necessary') return; // Cannot disable necessary cookies
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  if (!showBanner && !showPreferences) return null;

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-black/60 z-[9998]" />
      
      {/* Cookie Preferences Modal */}
      {showPreferences ? (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <div className="bg-[var(--bg-secondary)] border border-white/10 rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-white/10 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Settings size={20} className="text-[#2a5ff6]" />
                <h2 className="text-xl font-semibold text-white">Cookie Preferences</h2>
              </div>
              <button 
                onClick={() => setShowPreferences(false)}
                className="text-white/60 hover:text-white"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              <p className="text-white/70 text-sm">
                We use cookies to enhance your browsing experience, serve personalized content, and analyze our traffic. 
                You can customize your preferences below.
              </p>

              {/* Necessary Cookies */}
              <div className="p-4 bg-white/5 rounded-xl">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-white">Necessary Cookies</h3>
                  <span className="text-xs bg-[#2a5ff6]/20 text-[#2a5ff6] px-2 py-1 rounded">Always Active</span>
                </div>
                <p className="text-sm text-white/60">
                  These cookies are essential for the website to function properly. They enable basic functions like 
                  page navigation and access to secure areas.
                </p>
              </div>

              {/* Analytics Cookies */}
              <div className="p-4 bg-white/5 rounded-xl">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-white">Analytics Cookies</h3>
                  <button
                    onClick={() => handlePreferenceChange('analytics')}
                    className={`w-12 h-6 rounded-full transition-colors ${
                      preferences.analytics ? 'bg-[#2a5ff6]' : 'bg-white/20'
                    }`}
                  >
                    <div className={`w-5 h-5 bg-white rounded-full transition-transform ${
                      preferences.analytics ? 'translate-x-6' : 'translate-x-0.5'
                    }`} />
                  </button>
                </div>
                <p className="text-sm text-white/60">
                  These cookies help us understand how visitors interact with our website by collecting and reporting 
                  information anonymously. This includes Google Analytics.
                </p>
              </div>

              {/* Marketing Cookies */}
              <div className="p-4 bg-white/5 rounded-xl">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-white">Marketing Cookies</h3>
                  <button
                    onClick={() => handlePreferenceChange('marketing')}
                    className={`w-12 h-6 rounded-full transition-colors ${
                      preferences.marketing ? 'bg-[#2a5ff6]' : 'bg-white/20'
                    }`}
                  >
                    <div className={`w-5 h-5 bg-white rounded-full transition-transform ${
                      preferences.marketing ? 'translate-x-6' : 'translate-x-0.5'
                    }`} />
                  </button>
                </div>
                <p className="text-sm text-white/60">
                  These cookies are used to track visitors across websites to display relevant advertisements 
                  based on their browsing habits.
                </p>
              </div>
            </div>

            <div className="p-6 border-t border-white/10 flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleRejectAll}
                className="flex-1 py-3 px-4 border border-white/20 text-white rounded-xl hover:bg-white/5 transition-colors"
              >
                Reject All
              </button>
              <button
                onClick={handleSavePreferences}
                className="flex-1 py-3 px-4 bg-[#2a5ff6] text-white rounded-xl hover:bg-[#4d7ff8] transition-colors"
              >
                Save Preferences
              </button>
            </div>
          </div>
        </div>
      ) : (
        /* Main Cookie Banner */
        <div className="fixed bottom-0 left-0 right-0 z-[9999] p-4 md:p-6">
          <div className="max-w-4xl mx-auto bg-[var(--bg-secondary)] border border-white/10 rounded-2xl p-6 shadow-2xl">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-10 h-10 bg-[#2a5ff6]/20 flex items-center justify-center rounded-xl flex-shrink-0">
                <Cookie size={20} className="text-[#2a5ff6]" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">We value your privacy</h3>
                <p className="text-sm text-white/70">
                  We use cookies to enhance your browsing experience, serve personalized content, and analyze our traffic. 
                  By clicking "Accept All", you consent to our use of cookies. You can manage your preferences or learn more 
                  in our{' '}
                  <Link to="/privacy" className="text-[#2a5ff6] hover:underline">Privacy Policy</Link>.
                </p>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleRejectAll}
                className="py-3 px-6 border border-white/20 text-white rounded-xl hover:bg-white/5 transition-colors text-sm font-medium"
              >
                Reject All
              </button>
              <button
                onClick={() => setShowPreferences(true)}
                className="py-3 px-6 border border-white/20 text-white rounded-xl hover:bg-white/5 transition-colors text-sm font-medium flex items-center justify-center gap-2"
              >
                <Settings size={16} />
                Manage Preferences
              </button>
              <button
                onClick={handleAcceptAll}
                className="py-3 px-6 bg-[#2a5ff6] text-white rounded-xl hover:bg-[#4d7ff8] transition-colors text-sm font-medium flex-1 sm:flex-none"
              >
                Accept All
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Export a function to open cookie preferences from anywhere (e.g., footer link)
export const openCookiePreferences = () => {
  localStorage.removeItem(CONSENT_KEY);
  window.location.reload();
};

export default CookieConsentBanner;
